import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { formatCOP, formatDate } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import Statistics from './Statistics';
import { useNavigate } from 'react-router-dom';
import { Users, DollarSign, ArrowRight, TrendingUp, BarChart3, Building2, Target, Eye, FileText, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Wallet, ArrowDownLeft, ArrowUpRight, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const COLORS = ['#A38560', '#6BCB77', '#C4A87C', '#1E4D40', '#8B6F47', '#390517'];

function FinanceCard({ label, value, color = '#E0E0E0' }) {
  return (
    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
      <p className="text-xs uppercase tracking-wider text-white/40 mb-2">{label}</p>
      <p className="font-outfit text-xl font-bold tracking-tight tabular-nums" style={{ color }}>{formatCOP(value)}</p>
    </div>
  );
}

function DealFinanceCard({ deal }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full p-5 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
        <div className="flex items-center gap-4 text-left">
          <Badge className={deal.deal_type === 'venta' ? 'bg-[#A38560]/10 text-[#A38560] border-0' : 'bg-[#6BCB77]/10 text-[#6BCB77] border-0'}>
            {deal.deal_type === 'venta' ? 'Venta' : 'Arriendo'}
          </Badge>
          <div>
            <p className="font-medium text-white text-sm">{deal.property_address}</p>
            <p className="text-xs text-white/40">{deal.client_name} / {deal.owner_name} — Cierre: {formatDate(deal.closing_date)}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="font-outfit text-lg font-bold text-white tabular-nums">{formatCOP(deal.closing_value)}</p>
            <p className="text-xs text-white/40">Valor cierre</p>
          </div>
          {open ? <ChevronUp size={18} className="text-white/40" /> : <ChevronDown size={18} className="text-white/40" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
          {/* Commission Breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-[#16302B] rounded-xl border border-white/5">
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Com. Total</p>
              <p className="font-outfit font-bold text-[#A38560] tabular-nums">{formatCOP(deal.total_commission)}</p>
            </div>
            <div className="p-3 bg-[#16302B] rounded-xl border border-white/5">
              <p className="text-[10px] text-white/40 uppercase tracking-wider">Agencia SELECTO</p>
              <p className="font-outfit font-bold text-white tabular-nums">{formatCOP(deal.agency_commission)}</p>
              <p className="text-[10px] mt-0.5">
                <span className="text-[#6BCB77]">Recibido: {formatCOP(deal.agency_received)}</span>
                {deal.agency_commission - deal.agency_received > 0 && (
                  <span className="text-red-400 ml-2">Pend: {formatCOP(deal.agency_commission - deal.agency_received)}</span>
                )}
              </p>
            </div>
            {deal.client_agent_name && (
              <div className="p-3 bg-[#16302B] rounded-xl border border-white/5">
                <p className="text-[10px] text-white/40 uppercase tracking-wider">Broker Cliente</p>
                <p className="text-xs text-[#A38560] mb-0.5">{deal.client_agent_name}</p>
                <p className="font-outfit font-bold text-white tabular-nums">{formatCOP(deal.client_broker_commission)}</p>
                <p className="text-[10px] mt-0.5">
                  <span className="text-[#6BCB77]">Recibido: {formatCOP(deal.client_broker_received)}</span>
                </p>
              </div>
            )}
            {deal.owner_agent_name && (
              <div className="p-3 bg-[#16302B] rounded-xl border border-white/5">
                <p className="text-[10px] text-white/40 uppercase tracking-wider">Broker Propietario</p>
                <p className="text-xs text-[#A38560] mb-0.5">{deal.owner_agent_name}</p>
                <p className="font-outfit font-bold text-white tabular-nums">{formatCOP(deal.owner_broker_commission)}</p>
                <p className="text-[10px] mt-0.5">
                  <span className="text-[#6BCB77]">Recibido: {formatCOP(deal.owner_broker_received)}</span>
                </p>
              </div>
            )}
          </div>
          {deal.external_broker_name && (
            <div className="text-xs text-white/50 bg-[#16302B] p-2 rounded-lg border border-white/5">
              Broker Externo: <span className="text-white">{deal.external_broker_name}</span> — {formatCOP(deal.external_commission)}
            </div>
          )}

          {/* All Payments */}
          {deal.all_payments && deal.all_payments.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-2">Historial de Pagos</h4>
              <div className="space-y-1.5">
                {deal.all_payments.map((p, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-[#16302B] rounded-lg text-xs border border-white/5">
                    <div className="flex items-center gap-2">
                      {p.type === 'commission_receipt' && <ArrowDownLeft size={12} className="text-[#6BCB77]" />}
                      {p.type === 'disbursement' && <ArrowUpRight size={12} className="text-[#A38560]" />}
                      {p.type === 'apartment' && <DollarSign size={12} className="text-white/40" />}
                      {p.type === 'commission' && <DollarSign size={12} className="text-[#A38560]" />}
                      <span className="text-white/80">{p.detail}</span>
                      <span className="text-white/30">({p.payment_method})</span>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                      <span className="font-outfit font-medium text-white tabular-nums">{formatCOP(p.amount)}</span>
                      <span className="text-white/40">{formatDate(p.date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(!deal.all_payments || deal.all_payments.length === 0) && (
            <p className="text-xs text-white/30 italic">No hay pagos registrados para este negocio.</p>
          )}
        </div>
      )}
    </div>
  );
}

function DiffIndicator({ current, previous, isCurrency = false }) {
  const diff = current - previous;
  if (diff === 0) return <p className="text-[10px] text-white/30 mt-1">= sin cambio</p>;
  const isUp = diff > 0;
  return (
    <p className={`text-[10px] mt-1 font-medium ${isUp ? 'text-[#6BCB77]' : 'text-red-400'}`}>
      {isUp ? '▲' : '▼'} {isCurrency ? formatCOP(Math.abs(diff)) : Math.abs(diff)}
    </p>
  );
}

export default function AdminTracking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState([]);
  const [selectedBroker, setSelectedBroker] = useState('');
  const [brokerDeals, setBrokerDeals] = useState([]);
  const [brokerTab, setBrokerTab] = useState('estadisticas');
  const [finance, setFinance] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [consolidated, setConsolidated] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [perfMonth, setPerfMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });
  const [brokerGoals, setBrokerGoals] = useState([]);

  useEffect(() => { api.get('/users').then(setBrokers).catch(() => {}); }, []);

  useEffect(() => {
    if (selectedBroker) {
      api.get(`/deals?user_id=${selectedBroker}`).then(setBrokerDeals).catch(() => {});
    }
  }, [selectedBroker]);

  const loadFinance = useCallback(() => {
    let url = '/admin/finance';
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (params.length) url += '?' + params.join('&');
    api.get(url).then(setFinance).catch(() => {});
  }, [startDate, endDate]);

  const loadConsolidated = useCallback(() => {
    api.get('/stats/consolidated').then(setConsolidated).catch(() => {});
  }, []);

  const loadPerformance = useCallback((month) => {
    api.get(`/admin/performance?month=${month}`).then(setPerformance).catch(() => {});
  }, []);

  const loadBrokerGoals = useCallback(() => {
    api.get('/admin/broker-goals').then(setBrokerGoals).catch(() => {});
  }, []);

  const changeMonth = (delta) => {
    const [y, m] = perfMonth.split('-').map(Number);
    let newM = m + delta, newY = y;
    if (newM > 12) { newM = 1; newY++; }
    if (newM < 1) { newM = 12; newY--; }
    const next = `${newY}-${String(newM).padStart(2, '0')}`;
    setPerfMonth(next);
  };

  useEffect(() => { loadFinance(); loadConsolidated(); loadBrokerGoals(); loadPerformance(perfMonth); }, [loadFinance, loadConsolidated, loadBrokerGoals, loadPerformance, perfMonth]);
  useEffect(() => { loadPerformance(perfMonth); }, [perfMonth, loadPerformance]);

  if (user?.role !== 'admin') return <div className="p-8 text-center text-white/50">Solo para administradores.</div>;

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl";
  const selectContentCls = "bg-[#0A1F1A] border-white/10 text-white";

  return (
    <div data-testid="admin-tracking-page" className="space-y-6">
      <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Seguimiento Empresa</h1>

      <Tabs defaultValue="broker-view">
        <TabsList className="bg-[#0A1F1A] border border-white/5 h-auto p-1 rounded-xl flex flex-wrap gap-1">
          <TabsTrigger value="broker-view" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-2 text-sm rounded-lg flex-1 min-w-[100px] py-2.5" data-testid="tab-broker-view">
            <Users size={14} /> Vista Broker
          </TabsTrigger>
          <TabsTrigger value="rendimiento" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-2 text-sm rounded-lg flex-1 min-w-[100px] py-2.5" data-testid="tab-rendimiento">
            <Activity size={14} /> Rendimiento
          </TabsTrigger>
          <TabsTrigger value="metas" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-2 text-sm rounded-lg flex-1 min-w-[100px] py-2.5" data-testid="tab-metas">
            <Target size={14} /> Metas Brokers
          </TabsTrigger>
          <TabsTrigger value="consolidated" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-2 text-sm rounded-lg flex-1 min-w-[100px] py-2.5" data-testid="tab-consolidated">
            <BarChart3 size={14} /> Consolidadas
          </TabsTrigger>
          <TabsTrigger value="finance" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-2 text-sm rounded-lg flex-1 min-w-[100px] py-2.5" data-testid="tab-finance">
            <DollarSign size={14} /> Finanzas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="broker-view">
          <div className="space-y-6 mt-4">
            <Select value={selectedBroker} onValueChange={setSelectedBroker}>
              <SelectTrigger data-testid="broker-selector" className={`w-72 ${inputCls}`}><SelectValue placeholder="Seleccionar miembro del equipo..." /></SelectTrigger>
              <SelectContent className={selectContentCls}>{brokers.map(b => <SelectItem key={b.id} value={b.id}>{b.name} ({b.role})</SelectItem>)}</SelectContent>
            </Select>

            {selectedBroker && (
              <Tabs value={brokerTab} onValueChange={setBrokerTab}>
                <TabsList className="bg-[#0A1F1A] border border-white/5 h-12 p-1 rounded-xl">
                  <TabsTrigger value="estadisticas" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 text-sm rounded-lg">Estadisticas</TabsTrigger>
                  <TabsTrigger value="negocios" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 text-sm rounded-lg">Negocios</TabsTrigger>
                </TabsList>

                <TabsContent value="estadisticas">
                  <div className="mt-4"><Statistics userId={selectedBroker} /></div>
                </TabsContent>

                <TabsContent value="negocios">
                  <div className="mt-4 space-y-4">
                    {brokerDeals.length === 0 ? (
                      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-8 text-center text-white/50">
                        Este broker no tiene negocios registrados.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {brokerDeals.map(deal => (
                          <div key={deal.id} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5 hover:border-[#A38560]/30 transition-all">
                            <div className="flex items-center justify-between mb-2">
                              <Badge className={deal.deal_type === 'venta' ? 'bg-[#A38560]/10 text-[#A38560] border-0' : 'bg-[#6BCB77]/10 text-[#6BCB77] border-0'}>
                                {deal.deal_type === 'venta' ? 'Venta' : 'Arriendo'}
                              </Badge>
                              <span className="text-xs text-white/40">{formatDate(deal.created_at)}</span>
                            </div>
                            <p className="font-outfit text-xl font-bold text-white tabular-nums">{formatCOP(deal.closing_value)}</p>
                            <p className="text-sm text-white/50 mt-1">{deal.property_address}</p>
                            <button onClick={() => navigate(`/negocios/${deal.id}`)} className="mt-3 w-full flex items-center justify-center gap-2 py-2 bg-[#16302B] text-white text-xs font-medium rounded-xl hover:bg-[#1E4D40] transition-colors border border-white/5">
                              Ver Detalle <ArrowRight size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </TabsContent>

        {/* ============ TAB: Rendimiento ============ */}
        <TabsContent value="rendimiento" className="space-y-4 mt-4">
          {/* Month Selector */}
          <div className="flex items-center justify-between bg-[#0A1F1A] rounded-2xl border border-white/5 p-4" data-testid="performance-month-selector">
            <button onClick={() => changeMonth(-1)} className="p-2 rounded-xl bg-[#16302B] hover:bg-[#1A3B32] text-white transition-colors" data-testid="perf-prev-month"><ChevronLeft size={20} /></button>
            <div className="text-center">
              <p className="font-outfit text-lg font-medium text-white">{performance?.month ? new Date(performance.month + '-15').toLocaleDateString('es-CO', { month: 'long', year: 'numeric' }) : ''}</p>
              {performance?.prev_month && <p className="text-xs text-white/40">vs {new Date(performance.prev_month + '-15').toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}</p>}
            </div>
            <button onClick={() => changeMonth(1)} className="p-2 rounded-xl bg-[#16302B] hover:bg-[#1A3B32] text-white transition-colors" data-testid="perf-next-month"><ChevronRight size={20} /></button>
          </div>

          {/* Monthly Rankings */}
          {performance?.rankings && Object.keys(performance.rankings).length > 0 && (
            <div className="bg-[#0D2922] rounded-2xl border border-white/5 p-4 lg:p-5" data-testid="monthly-rankings">
              <h2 className="font-outfit text-sm font-medium text-[#A38560] tracking-wide uppercase mb-3">Ranking del Mes</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {performance.rankings.most_visits && (
                  <div className="bg-[#0A1F1A] rounded-xl border border-white/5 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Mas Visitas</p>
                    <p className="font-outfit text-lg font-bold text-white">{performance.rankings.most_visits.value}</p>
                    <p className="text-xs text-[#A38560] font-medium truncate">{performance.rankings.most_visits.name}</p>
                  </div>
                )}
                {performance.rankings.most_offers && (
                  <div className="bg-[#0A1F1A] rounded-xl border border-white/5 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Mas Ofertas</p>
                    <p className="font-outfit text-lg font-bold text-[#A38560]">{performance.rankings.most_offers.value}</p>
                    <p className="text-xs text-[#A38560] font-medium truncate">{performance.rankings.most_offers.name}</p>
                  </div>
                )}
                {performance.rankings.most_closings && (
                  <div className="bg-[#0A1F1A] rounded-xl border border-white/5 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Mas Cierres</p>
                    <p className="font-outfit text-lg font-bold text-[#6BCB77]">{performance.rankings.most_closings.value}</p>
                    <p className="text-xs text-[#A38560] font-medium truncate">{performance.rankings.most_closings.name}</p>
                  </div>
                )}
                {performance.rankings.best_v2o && (
                  <div className="bg-[#0A1F1A] rounded-xl border border-white/5 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Visitas a Ofertas</p>
                    <p className="font-outfit text-lg font-bold text-[#A38560]">{performance.rankings.best_v2o.value}%</p>
                    <p className="text-xs text-[#A38560] font-medium truncate">{performance.rankings.best_v2o.name}</p>
                  </div>
                )}
                {performance.rankings.best_o2c && (
                  <div className="bg-[#0A1F1A] rounded-xl border border-white/5 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Ofertas a Cierres</p>
                    <p className="font-outfit text-lg font-bold text-[#6BCB77]">{performance.rankings.best_o2c.value}%</p>
                    <p className="text-xs text-[#A38560] font-medium truncate">{performance.rankings.best_o2c.name}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Broker Performance Cards */}
          {performance?.brokers?.filter(b => b.role !== 'admin' || b.current.closings > 0 || b.previous.closings > 0).map(b => {
            const c = b.current, p = b.previous;
            const closingDiff = c.closings - p.closings;
            const valueDiff = c.closing_value - p.closing_value;
            return (
              <div key={b.id} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5" data-testid={`perf-broker-${b.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-outfit text-base font-medium text-white">{b.name}</h3>
                  <Badge className="bg-[#A38560]/10 text-[#A38560] border-0 text-xs">{b.role === 'admin' ? 'Admin' : 'Broker'}</Badge>
                </div>
                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  <div className="bg-[#16302B] rounded-xl p-3 text-center border border-white/5">
                    <p className="font-outfit text-2xl font-bold text-white">{c.visits}</p>
                    <p className="text-xs text-white/40 mt-1">Visitas</p>
                    <DiffIndicator current={c.visits} previous={p.visits} />
                  </div>
                  <div className="bg-[#16302B] rounded-xl p-3 text-center border border-white/5">
                    <p className="font-outfit text-2xl font-bold text-white">{c.offers}</p>
                    <p className="text-xs text-white/40 mt-1">Ofertas</p>
                    <DiffIndicator current={c.offers} previous={p.offers} />
                  </div>
                  <div className="bg-[#16302B] rounded-xl p-3 text-center border border-white/5">
                    <p className="font-outfit text-2xl font-bold text-[#6BCB77]">{c.closings}</p>
                    <p className="text-xs text-white/40 mt-1">Cierres</p>
                    <DiffIndicator current={c.closings} previous={p.closings} />
                  </div>
                  <div className="bg-[#16302B] rounded-xl p-3 text-center border border-white/5">
                    <p className="font-outfit text-lg font-bold text-[#A38560] tabular-nums">{formatCOP(c.closing_value)}</p>
                    <p className="text-xs text-white/40 mt-1">Valor Cierres</p>
                    <DiffIndicator current={c.closing_value} previous={p.closing_value} isCurrency />
                  </div>
                </div>
                {/* Conversion Funnel */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 text-center p-2 bg-[#16302B] rounded-xl border border-white/5">
                    <p className="font-outfit text-lg font-bold text-white">{c.visits}</p>
                    <p className="text-[10px] text-white/40">Visitas</p>
                  </div>
                  <div className="flex flex-col items-center shrink-0">
                    <ArrowRight size={14} className="text-white/20" />
                    <span className="text-[10px] text-[#A38560] font-bold">{c.v2o}%</span>
                  </div>
                  <div className="flex-1 text-center p-2 bg-[#16302B] rounded-xl border border-white/5">
                    <p className="font-outfit text-lg font-bold text-[#A38560]">{c.offers}</p>
                    <p className="text-[10px] text-white/40">Ofertas</p>
                  </div>
                  <div className="flex flex-col items-center shrink-0">
                    <ArrowRight size={14} className="text-white/20" />
                    <span className="text-[10px] text-[#6BCB77] font-bold">{c.o2c}%</span>
                  </div>
                  <div className="flex-1 text-center p-2 bg-[#16302B] rounded-xl border border-white/5">
                    <p className="font-outfit text-lg font-bold text-[#6BCB77]">{c.closings}</p>
                    <p className="text-[10px] text-white/40">Cierres</p>
                  </div>
                </div>
              </div>
            );
          })}
          {performance?.brokers?.length === 0 && <p className="text-sm text-white/40 text-center py-8">No hay datos de rendimiento para este mes.</p>}
        </TabsContent>

        {/* ============ TAB: Metas Brokers ============ */}
        <TabsContent value="metas" className="space-y-4 mt-4">
          <div className="bg-[#0D2922] rounded-2xl border border-white/5 p-4 lg:p-6" data-testid="broker-goals-section">
            <h2 className="font-outfit text-base lg:text-lg font-medium text-[#A38560] tracking-wide uppercase mb-4">Progreso de Metas por Broker</h2>
            <div className="space-y-3">
              {brokerGoals.filter(b => b.role !== 'admin').map(b => {
                const goalProgress = b.income_goal > 0 ? Math.min((b.monthly_avg / b.income_goal) * 100, 100) : 0;
                return (
                  <div key={b.id} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5" data-testid={`broker-goal-${b.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-outfit text-base font-medium text-white">{b.name}</h3>
                      <span className="text-sm text-white/40">Meta: {b.income_goal > 0 ? formatCOP(b.income_goal) + '/mes' : 'Sin meta'}</span>
                    </div>
                    {b.income_goal > 0 ? (
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-sm">
                            <span className="text-white/50">Generadas / 12</span>
                            <span className="font-medium text-[#A38560] tabular-nums">{formatCOP(b.monthly_avg)}</span>
                          </div>
                          <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/10">
                            <div className="h-full rounded-full bg-gradient-to-r from-[#A38560] to-[#8B6F47] transition-all duration-1000 ease-out"
                              style={{ width: `${goalProgress}%` }} />
                          </div>
                          <span className="text-xs text-white/40">Generadas: {formatCOP(b.total_commission_year)} — {goalProgress.toFixed(1)}%</span>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-sm">
                            <span className="text-white/50">Recibidas / 12</span>
                            <span className="font-medium text-[#6BCB77] tabular-nums">{formatCOP(b.received_monthly_avg || 0)}</span>
                          </div>
                          <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/10">
                            <div className="h-full rounded-full bg-gradient-to-r from-[#6BCB77] to-[#4EAAAA] transition-all duration-1000 ease-out"
                              style={{ width: `${Math.min(((b.received_monthly_avg || 0) / b.income_goal) * 100, 100)}%` }} />
                          </div>
                          <span className="text-xs text-white/40">Recibidas: {formatCOP(b.received_this_year || 0)} — {(((b.received_monthly_avg || 0) / b.income_goal) * 100).toFixed(1)}%</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-white/40">Este broker aun no ha definido su meta de ingresos.</p>
                    )}
                  </div>
                );
              })}
              {brokerGoals.filter(b => b.role !== 'admin').length === 0 && <p className="text-sm text-white/40 text-center py-4">No hay brokers registrados.</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="consolidated">
          <div className="space-y-6 mt-4">
            {consolidated ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="consolidated-metrics">
                  <FinanceCard label="Ventas Totales" value={consolidated.total_sales} color="#A38560" />
                  <FinanceCard label="Arriendos Totales" value={consolidated.total_rentals} color="#6BCB77" />
                  <FinanceCard label="Comisiones Totales" value={consolidated.total_commissions} color="#E0E0E0" />
                  <FinanceCard label="Comisiones Agencia" value={consolidated.agency_commissions} color="#C4A87C" />
                  <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
                    <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Negocios Cerrados</p>
                    <p className="font-outfit text-xl font-bold text-white">{consolidated.deals_closed}</p>
                    <p className="text-xs text-white/40 mt-1">{consolidated.sales_count} ventas / {consolidated.rentals_count} arriendos</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-wider text-white/40">Ticket Promedio Venta</p>
                      <TrendingUp size={16} className="text-[#A38560]" />
                    </div>
                    <p className="font-outfit text-lg font-bold text-white tabular-nums">{formatCOP(consolidated.avg_ticket_sales)}</p>
                  </div>
                  <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-wider text-white/40">Ticket Promedio Arriendo</p>
                      <TrendingUp size={16} className="text-[#6BCB77]" />
                    </div>
                    <p className="font-outfit text-lg font-bold text-white tabular-nums">{formatCOP(consolidated.avg_ticket_rentals)}</p>
                  </div>
                  <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
                    <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Total Captaciones</p>
                    <p className="font-outfit text-lg font-bold text-[#6BCB77]">{consolidated.total_captures}</p>
                  </div>
                  <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5">
                    <p className="text-xs uppercase tracking-wider text-white/40 mb-2">Ratio Visita a Oferta</p>
                    <p className="font-outfit text-lg font-bold text-[#A38560]">{consolidated.conversion.visit_to_offer_ratio}%</p>
                  </div>
                </div>

                <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-6" data-testid="consolidated-funnel">
                  <h2 className="font-outfit text-lg font-medium text-white mb-4">Embudo de Conversion Global</h2>
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    <div className="flex-1 text-center p-5 bg-[#16302B] rounded-xl border border-white/5">
                      <Eye size={24} className="text-white/40 mx-auto mb-2" />
                      <p className="font-outfit text-3xl font-bold text-white">{consolidated.conversion.visits}</p>
                      <p className="text-sm text-white/40 mt-1">Visitas</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <ArrowRight size={20} className="text-white/20" />
                      <span className="text-xs text-[#A38560] font-bold mt-1">{consolidated.conversion.visit_to_offer_ratio}%</span>
                    </div>
                    <div className="flex-1 text-center p-5 bg-[#16302B] rounded-xl border border-white/5">
                      <FileText size={24} className="text-[#A38560] mx-auto mb-2" />
                      <p className="font-outfit text-3xl font-bold text-[#A38560]">{consolidated.conversion.offers}</p>
                      <p className="text-sm text-white/40 mt-1">Ofertas</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <ArrowRight size={20} className="text-white/20" />
                      <span className="text-xs text-[#6BCB77] font-bold mt-1">{consolidated.conversion.offer_to_closing_ratio}%</span>
                    </div>
                    <div className="flex-1 text-center p-5 bg-[#16302B] rounded-xl border border-white/5">
                      <DollarSign size={24} className="text-[#6BCB77] mx-auto mb-2" />
                      <p className="font-outfit text-3xl font-bold text-[#6BCB77]">{consolidated.conversion.closings}</p>
                      <p className="text-sm text-white/40 mt-1">Cierres</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {consolidated.captures_by_month && consolidated.captures_by_month.length > 0 && (
                    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-6" data-testid="consolidated-captures-chart">
                      <h2 className="font-outfit text-lg font-medium text-white mb-4 flex items-center gap-2">
                        <Building2 size={18} className="text-[#6BCB77]" /> Captaciones por Mes
                      </h2>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={consolidated.captures_by_month} margin={{ left: 0, right: 20 }}>
                          <XAxis dataKey="month" stroke="#5A6B64" fontSize={11} />
                          <YAxis allowDecimals={false} stroke="#5A6B64" fontSize={11} />
                          <Tooltip contentStyle={{ background: '#0A1F1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#E0E0E0' }} formatter={(value) => [value, 'Captaciones']} />
                          <Bar dataKey="count" radius={[8, 8, 0, 0]} barSize={32}>
                            {consolidated.captures_by_month.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  {consolidated.source_distribution && consolidated.source_distribution.length > 0 && (
                    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-6" data-testid="consolidated-sources">
                      <h2 className="font-outfit text-lg font-medium text-white mb-4">Distribucion de Fuentes</h2>
                      <div className="space-y-2 max-h-[200px] overflow-auto">
                        {consolidated.source_distribution.map((s, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-[#16302B] rounded-xl border border-white/5">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                              <span className="text-sm text-white">{s.source}</span>
                            </div>
                            <span className="font-outfit font-bold text-white">{s.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse font-outfit text-lg text-white/50">Cargando estadisticas consolidadas...</div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="finance">
          <div className="space-y-6 mt-4">
            {/* Date filter */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 sm:gap-4">
              <div>
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Desde</Label>
                <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
              </div>
              <div>
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Hasta</Label>
                <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={inputCls} />
              </div>
              <button onClick={loadFinance} data-testid="filter-finance-btn"
                className="bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-5 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98] h-11">
                Filtrar
              </button>
            </div>

            {finance && (
              <>
                {/* Top Summary - Agency Commissions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="finance-agency-summary">
                  <div className="bg-[#0A1F1A] rounded-2xl border border-[#A38560]/20 p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Wallet size={20} className="text-[#A38560]" />
                      <p className="text-xs uppercase tracking-wider text-white/50">Comisiones Agencia SELECTO Generadas</p>
                    </div>
                    <p className="font-outfit text-3xl font-bold text-[#A38560] tabular-nums">{formatCOP(finance.agency_total_generated)}</p>
                  </div>
                  <div className="bg-[#0A1F1A] rounded-2xl border border-[#6BCB77]/20 p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign size={20} className="text-[#6BCB77]" />
                      <p className="text-xs uppercase tracking-wider text-white/50">Pagado Efectivamente a la Agencia</p>
                    </div>
                    <p className="font-outfit text-3xl font-bold text-[#6BCB77] tabular-nums">{formatCOP(finance.agency_total_received)}</p>
                    {finance.agency_total_generated - finance.agency_total_received > 0 && (
                      <p className="text-sm text-red-400 mt-2 tabular-nums">
                        Pendiente: {formatCOP(finance.agency_total_generated - finance.agency_total_received)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Per-Deal Breakdown */}
                <div>
                  <h2 className="font-outfit text-lg font-medium text-white mb-3 flex items-center gap-2">
                    <Building2 size={18} className="text-[#A38560]" />
                    Desglose por Negocio ({finance.deals.length})
                  </h2>
                  {finance.deals.length > 0 ? (
                    <div className="space-y-3" data-testid="finance-deals-list">
                      {finance.deals.map(deal => (
                        <DealFinanceCard key={deal.id} deal={deal} />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-8 text-center text-white/50">
                      No hay negocios en este rango de fechas.
                    </div>
                  )}
                </div>

                {/* Broker Consolidation */}
                {finance.broker_consolidation && finance.broker_consolidation.length > 0 && (
                  <div data-testid="finance-broker-consolidation">
                    <h2 className="font-outfit text-lg font-medium text-white mb-3 flex items-center gap-2">
                      <Users size={18} className="text-[#A38560]" />
                      Consolidado por Broker
                    </h2>
                    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                        <thead>
                          <tr className="bg-[#16302B] border-b border-white/10">
                            <th className="text-left py-3 px-4 text-white/40 text-xs uppercase tracking-wider font-medium">Broker</th>
                            <th className="text-right py-3 px-4 text-white/40 text-xs uppercase tracking-wider font-medium">Comision Generada</th>
                            <th className="text-right py-3 px-4 text-white/40 text-xs uppercase tracking-wider font-medium">Recibido Efectivamente</th>
                            <th className="text-right py-3 px-4 text-white/40 text-xs uppercase tracking-wider font-medium">Pendiente</th>
                          </tr>
                        </thead>
                        <tbody>
                          {finance.broker_consolidation.map((b, i) => (
                            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                              <td className="py-3 px-4 font-medium text-white">{b.name}</td>
                              <td className="py-3 px-4 text-right tabular-nums text-[#A38560] font-medium">{formatCOP(b.commission_generated)}</td>
                              <td className="py-3 px-4 text-right tabular-nums text-[#6BCB77] font-medium">{formatCOP(b.commission_received)}</td>
                              <td className="py-3 px-4 text-right tabular-nums">
                                {b.commission_generated - b.commission_received > 0 ? (
                                  <span className="text-red-400 font-medium">{formatCOP(b.commission_generated - b.commission_received)}</span>
                                ) : (
                                  <span className="text-[#6BCB77]">$ 0 COP</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
